import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, Clock } from "lucide-react";
import { PageHero } from "@/components/PageChrome";
import CTABanner from "@/components/CTABanner";
import { useSEO } from "@/lib/seo";
import { formatDate } from "@/lib/categories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function News() {
    const [posts, setPosts] = useState(null);

    useEffect(() => {
        let alive = true;
        axios.get(`${API}/posts`, { params: { category: "news" } })
            .then(({ data }) => { if (alive) setPosts(data); })
            .catch(() => { if (alive) setPosts([]); });
        return () => { alive = false; };
    }, []);

    useSEO({
        title: "News",
        description: "Announcements, recognitions, and firm news from Chesterton.",
        path: "/news",
    });

    return (
        <>
            <PageHero
                testId="news-hero"
                kicker="News"
                title={<>Firm news &amp; announcements.</>}
                intro="Recognitions, speaking engagements, publications, and updates from the firm."
            />

            <section className="pb-20 lg:pb-24 bg-acre-bg">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
                    {posts === null ? (
                        <div className="text-center text-acre-muted py-16" data-testid="news-loading">Loading…</div>
                    ) : posts.length === 0 ? (
                        <div className="border-y border-acre-line py-24 text-center" data-testid="news-empty">
                            <div className="text-[11px] uppercase tracking-[0.28em] text-acre-secondary mb-4">
                                Nothing here yet
                            </div>
                            <div className="font-serif-display text-3xl lg:text-4xl text-acre-secondary max-w-xl mx-auto leading-tight">
                                News items will appear here when the firm has something to announce.
                            </div>
                        </div>
                    ) : (
                        <ul className="divide-y divide-acre-line border-y border-acre-line" data-testid="news-list">
                            {posts.map((p, idx) => (
                                <li key={p.slug} data-testid={`news-row-${p.slug}`}>
                                    <Link
                                        to={`/blog/${p.slug}`}
                                        className="group grid grid-cols-12 gap-4 lg:gap-8 py-8 lg:py-10 items-start hover:bg-[#FBFAF8] transition-colors"
                                    >
                                        <div className="col-span-2 lg:col-span-1 font-serif-display text-2xl lg:text-3xl leading-none pt-1">
                                            {String(idx + 1).padStart(2, "0")}
                                        </div>
                                        <div className="col-span-10 lg:col-span-4">
                                            <h3 className="font-serif-display text-2xl lg:text-3xl text-acre-ink leading-tight group-hover:text-acre-secondary transition-colors">
                                                {p.title}
                                            </h3>
                                            <div className="mt-3 flex items-center gap-5 text-xs text-acre-muted">
                                                <span className="flex items-center gap-2"><Calendar size={12} /> {formatDate(p.date)}</span>
                                                <span className="flex items-center gap-2"><Clock size={12} /> {p.read_time}</span>
                                            </div>
                                        </div>
                                        <div className="col-span-12 lg:col-span-7 mt-2 lg:mt-0">
                                            <p className="text-acre-muted text-base leading-relaxed max-w-2xl">
                                                {p.excerpt}
                                            </p>
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            <CTABanner />
        </>
    );
}
